import { Add, Close } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    FormControl,
    FormLabel,
    IconButton,
    MenuItem,
    Select,
    TextField,
    TextFieldProps,
    Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useEffect, useState } from 'react';
import { toast } from "react-toastify";
import { ReactComponent as CopyIcon } from "../../../assets/icons/CopyIcon.svg";
import {
    darkGrayColor,
    darkGreenColor,
    greenColor,
    lightGrayColor,
    redColor,
    StageHasHoursErrorFromResource,
    whiteColor
} from '../../constants/constants';
import { getStageHasHoursFromResource } from "../../core/http/httpClient";
import { StageResource } from '../../core/models/stageResource';
import {
    addAssignment,
    addStage,
    closeSaveProjectStageDialog,
    deleteAssignment,
    duplicateResource,
    fetchResources,
    fetchActiveResources,
    fetchRoles,
    selectResource,
    setResourceRole,
    setResourceStageEndDate,
    setResourceStageStartDate,
    setStageFinishDate,
    setStageName,
    setStageNumber,
    setStageStartDate,
    setWeekHours,
    updateStage
} from '../../core/state/dialogs/saveProjectDialogSlice';
import { useAppDispatch, useAppSelector } from '../../core/state/hooks';
import './projects.css';

export default function SaveProjectDialog() {
    const dispatch = useAppDispatch();
    const { stageDialogisOpen, selectedData, roles, stageDialogisCreating, selectedStageData, activeResources } = useAppSelector((state) => state.saveProjectDialog);
    const [errors, setErrors] = useState({stageName: false, stageNumber: false, stageText: false, stageResources: false, projectRoles: false});
    var [isRolMandatory, setIsRolMandatory] = useState(true);
    const styles ={
        greyLine: {border:"1.5px solid #F1F0F0", backgroundColor:"#F1F0F0"},
        secondGreyLine :{border:"1.5px solid #F1F0F0", backgroundColor:"#F1F0F0", mt:2, position:"relative"}
    }

    const [projectRoleIds, setProjectRoleIds] = React.useState<Record<number, number>>({});
    const [resourceStartDates, setResourceStartDates] = React.useState<Record<number, Date>>({});
    const [resourceEndDates, setResourceEndDates] = React.useState<Record<number, Date>>({});
    const successField = {backgroundColor:lightGrayColor,borderRadius:4,border:2,borderColor:lightGrayColor};
    const errorField = {backgroundColor:lightGrayColor,borderRadius:4,border:2,borderColor:redColor};

    useEffect(() => {
        dispatch(fetchRoles());
        setInitialProjectRoleIds(selectedStageData.stage.stageResources);
        setInitialDates(selectedStageData.stage.stageResources);
    },[dispatch,selectedStageData]);

    const isNumberDisabled = (number: number) => {
        return selectedData.Stages.some((stage) => stage.number === number);
    };

    const includeValidResources = () => {
        let validResource = true;
        selectedStageData.stage.stageResources?.forEach((resource: { resourceId: number | null; }) => {
            if(resource.resourceId === 0 || resource.resourceId == null){
                validResource = false;
            }
        });
        return validResource;
    }

    const resourcesHaveRole = () => {
        let hasRole = true;
        selectedStageData.stage.stageResources.forEach((resource) => {
            if (resource.projectRoleId === 0) {
                hasRole = false;
            }
        });
        return hasRole;
    }

    const removeResourceFromStage = async (resourceIndex: number) => {
        const stageHasHoursFromResource = await getStageHasHoursFromResource(selectedData.Stages[selectedStageData.index].id, selectedStageData.stage.stageResources[resourceIndex].resourceId);
        if (stageHasHoursFromResource) {
            toast.error(StageHasHoursErrorFromResource);
        }else{
            dispatch(deleteAssignment(resourceIndex))
        }
    }

    const onClose = () => {
        dispatch(closeSaveProjectStageDialog());
        setStyleSuccess();
    }

    const setStyleSuccess = () => {
        setErrors({stageName: false, stageNumber: false, stageText: false, stageResources: false, projectRoles: false});
    }

    const validateProjectStage = () => {

        let newErrors = {...errors};
        let isValid = true;

        const validateField = (condition: boolean, fieldName: keyof typeof newErrors) => {
            if (condition) {
                newErrors[fieldName] = true;
                isValid = false;
            } else {
                newErrors[fieldName] = false;
            }
        };
        const stageNumber = selectedStageData.stage.number;
        const stageName = selectedStageData.stage.name;
        const resources = selectedStageData.stage.stageResources;

        validateField(stageName === '', 'stageName');
        validateField(stageNumber === 0, 'stageNumber');
        validateField(!includeValidResources(), 'stageResources');
        validateField(!resourcesHaveRole() && isRolMandatory, 'projectRoles');
        validateField(resources?.length === 0, 'stageText');

        setErrors(newErrors);

        return isValid;
    }

    const disablePastStartDate = (date: any) => {
        return date.getTime() < new Date(selectedStageData.stage.startDate).getTime();
    };

    const saveNewProjectStage = () => {
        const isValid = validateProjectStage();
        if(isValid){
            if(stageDialogisCreating){
                dispatch(addStage())
                dispatch(closeSaveProjectStageDialog());
            }else{
                dispatch(updateStage())
                dispatch(closeSaveProjectStageDialog());
            }
        }
    }

    useEffect(() => {
        dispatch(fetchResources());
        dispatch(fetchActiveResources());
    }, [dispatch]);

    useEffect(() => {
        const handleWheel = (event: WheelEvent) => {
            if ((document.activeElement as HTMLInputElement)?.type === "number") {
                (document.activeElement as HTMLInputElement)?.blur();
            }
        };
        document.addEventListener("wheel", handleWheel);
    }, []);

    function duplicateProjectResource(resourceId: number) {
        let resource = selectedStageData.stage.stageResources.find(resource => resource.resourceId === resourceId);
        if(resource){
            dispatch(duplicateResource(resource));
        }
    }

    const setInitialProjectRoleIds = (stageResources: StageResource[]) => {
        const initialProjectRoleIds = stageResources.reduce((acc, resource, index) => ({
            ...acc,
            [index]: resource.projectRoleId, // Replace 'projectRoleId' with actual property name if different
        }), {});
        setProjectRoleIds(initialProjectRoleIds);
    }

    const setInitialDates = (stageResources: StageResource[]) => {
        const initialStartDates = stageResources.reduce((acc, resource, index) => ({
            ...acc,
            [index]: resource.startDate ? resource.startDate : selectedStageData.stage.startDate
        }), {});
        const initialEndDates = stageResources.reduce((acc, resource, index) => ({
            ...acc,
            [index]: resource.endDate ? resource.endDate : selectedStageData.stage.endDate
        }), {});
        setResourceStartDates(initialStartDates);
        setResourceEndDates(initialEndDates);
    }

    const handleStartDateChange = (id: number, date?: Date) => {
        if(date) {
            setResourceStartDates({
                ...resourceStartDates,
                [id]: date,
            });
        }
    };

    const handleEndDateChange = (id: number, date: Date) => {
        if(date) {
            setResourceEndDates({
                ...resourceEndDates,
                [id]: date,
            });
        }
    };

    const handleProjectRoleIdChange = (id: number, newProjectRoleId: number) => {
        if(newProjectRoleId !== 0 || !isRolMandatory) {
            setProjectRoleIds({
                ...projectRoleIds,
                [id]: newProjectRoleId,
            });
        }
    };

    const updateProjectResourceRole = (index: number, assignment: any, roleId: any) => {
        dispatch(setResourceRole({ index: index, assignment: assignment, projectRoleId: roleId}));
    }

    const setAllResourcesDates = () => {
        const changedStartDates = selectedStageData.stage.stageResources.reduce((acc, resource, index) => ({
            ...acc,
            [index]: selectedStageData.stage.startDate
        }), {});
        const changedEndDates = selectedStageData.stage.stageResources.reduce((acc, resource, index) => ({
            ...acc,
            [index]: selectedStageData.stage.endDate
        }), {});
        setResourceStartDates(changedStartDates);
        setResourceEndDates(changedEndDates);
    }

    return (
        <Dialog open={stageDialogisOpen} fullWidth maxWidth="xl">
            <DialogContent sx={{display:"flex",flexDirection:"column"}}>
                <DialogContent sx={{display:"flex",flexDirection:"row", pb:'0px !important', position:'relative', overflowY: 'visible'}}>
                    <Typography variant='h5' sx={{ borderBottom: 2, borderBottomColor: 'rgba(35, 31, 32, 0.1)', pb: 1, flex:1 }}>{stageDialogisCreating? 'Crear' : 'Editar'} Etapa</Typography>
                </DialogContent>

                <DialogContent sx={{display:"flex",flex: 1, justifyContent:"space-between",flexDirection:"row"}}>
                    <DialogContent sx={{display:"flex",flex: 0.45,flexDirection:"column", justifyContent:'space-around', overflowY:'initial', pb: '2px !important'}}>
                        <FormControl sx={{ flex: 0.35 }}>
                            <FormLabel sx={{ ml: 0.5, mb: 0.5 }}>Número etapa (*)</FormLabel>
                            <Select
                                sx={errors.stageNumber? errorField : successField}
                                id="stage"
                                value={selectedStageData.stage.number}
                                onChange={(event) => {
                                    dispatch(setStageNumber(event.target.value));
                                }}
                            >
                                {[...Array(20)].map((_, index) => (
                                    <MenuItem
                                        key={index + 1}
                                        value={index + 1}
                                        disabled={isNumberDisabled(index + 1)} // Check if the current number is in the stages list
                                    >
                                        {index + 1}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{flex:0.35}}>
                            <FormLabel sx={{ml:1, mb:0.5}}>Fecha</FormLabel>
                            <LocalizationProvider dateAdapter={AdapterDateFns} >
                                <Box sx={{backgroundColor:lightGrayColor,borderRadius:9}}>
                                    <DatePicker
                                        inputFormat="dd/MM/yyyy"
                                        value={selectedStageData.stage.startDate}
                                        onChange={(selectedDate: Date | null) => { dispatch(setStageStartDate(selectedDate)) }}
                                        renderInput={(params: TextFieldProps) => <TextField sx={{display:"flex", flex:1}} {...params} />}
                                    />
                                </Box>
                            </LocalizationProvider>
                        </FormControl>
                    </DialogContent>

                    <DialogContent sx={{display:"flex",flex: 0.45,flexDirection:"column", justifyContent:'space-between', overflowY:'initial', pb: '2px !important'}}>
                        <FormControl sx={{flex: 0.35}}>
                            <FormLabel sx={{ml:0.5, mb:0.5}}>Etapa (*)</FormLabel>
                            <TextField sx={errors.stageName? errorField : successField} id="stage"
                                value={selectedStageData.stage.name}
                                onChange={(event) => { dispatch(setStageName(event.target.value)); }} />
                        </FormControl>
                        <FormControl sx={{flex:0.35}}>
                            <FormLabel sx={{ml:1, mb:0.5}}></FormLabel>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <Box sx={{backgroundColor:lightGrayColor,borderRadius:9, flex: 1}}>
                                        <DatePicker
                                            inputFormat="dd/MM/yyyy"
                                            value={selectedStageData.stage.endDate}
                                            onChange={(selectedDate: Date | null) => { dispatch(setStageFinishDate(selectedDate)) }}
                                            shouldDisableDate={disablePastStartDate}
                                            renderInput={(params: TextFieldProps) => <TextField sx={{display:"flex", flex:1}} {...params} />}
                                        />
                                    </Box>
                                </LocalizationProvider>
                        </FormControl>
                    </DialogContent>
                    <FormControl sx={{flex:0.03, padding:0, justifyContent:'flex-end'}}>
                        <IconButton children={<CopyIcon />} onClick={() => {
                            setAllResourcesDates()
                        }}/>
                    </FormControl>
                </DialogContent>

                <DialogContent sx={{display:"flex",flexDirection:"row", pb:'0px !important', position:'relative', overflowY: 'visible'}}>
                    <Typography variant='h6' sx={{ml:0.5, borderBottom: 2, borderBottomColor: 'rgba(35, 31, 32, 0.1)', pb: 1, flex:1, fontSize:'16px !important'}}>Recursos (**)</Typography>
                </DialogContent>
                <DialogContent sx={{display:"flex", flexDirection:"column", flex:"1 50 auto !important",zIndex:10, minHeight:'200px'}}>
                    {
                        selectedStageData.stage.stageResources?.length > 0? selectedStageData.stage.stageResources.map((assignment: { resourceId: any; weeklyHours: unknown; startDate: Date, endDate: Date}, _index: number) => {
                            return (
                                <Box key={_index} sx={{display:"flex",flexDirection:"row", justifyContent:"space-between", mb:2}}>
                                    <Box display={"flex"} flexDirection={"column"} justifyContent={"end"}>
                                        <Button sx={{color:darkGrayColor,minWidth:10,alignSelf:"end", p:0.2 }}
                                                onClick={() => {removeResourceFromStage(Number(_index))}}
                                        >
                                            <Close sx={{fontSize:"1rem"}}/>
                                        </Button>
                                        <Box flex={0.15}></Box>
                                    </Box>
                                    <FormControl sx={{flex:0.45}} fullWidth>
                                        <FormLabel sx={{ml:0.5, mb:0.5}}>Recurso (*)</FormLabel>
                                        <Select
                                            id="resource-select"
                                            label="Resource"
                                            value={assignment.resourceId}
                                            onChange={(event) => { dispatch(selectResource({data:event.target.value, assignmentIndex:_index}))}}
                                            sx={errors.stageResources? errorField : successField}
                                        >
                                            {
                                                activeResources && activeResources.map((resource) => {
                                                    const isResourceAlreadySelected = selectedStageData.stage.stageResources.some((assignment: { resourceId: number; }, index: string | number | null | undefined) =>
                                                        assignment.resourceId === resource.id && index !== _index
                                                    );

                                                    return (
                                                        <MenuItem
                                                            value={resource.id}
                                                            key={resource.id}
                                                            disabled={isResourceAlreadySelected}
                                                        >
                                                            {resource.name + " " + resource.lastName}
                                                        </MenuItem>
                                                    )
                                                })
                                            }
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{flex:0.3, ml:1.5}}>
                                        <FormLabel sx={{ml:0.5, mb:0.5}}>Horas semanales</FormLabel>
                                        <TextField sx={{backgroundColor:lightGrayColor,borderRadius:9}} id="weekHours"
                                            type='number'
                                            value={assignment.weeklyHours}
                                            onChange={event => {
                                                const newValue = event.target.value !== '' ? Number(event.target.value) : 0;
                                                if (!isNaN(newValue)) {
                                                    dispatch(setWeekHours({data:newValue, assignmentIndex:_index}));
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ml:1, flex:0.4}} fullWidth>
                                            <FormLabel sx={{ml:0.5, mb:0.5}}>{isRolMandatory? "Rol (*)" : "Rol"}</FormLabel>
                                            <Select
                                                id="rol-select"
                                                label="Rol"
                                                value={projectRoleIds[_index] !== undefined ? String(projectRoleIds[_index]) : ''}
                                                onChange={(event) => {handleProjectRoleIdChange(_index, parseInt(event.target.value))}}
                                                sx={errors.projectRoles? errorField : successField}
                                                onBlur={(event) => {
                                                    updateProjectResourceRole(_index, assignment, parseInt(projectRoleIds[_index].toString()))
                                                }}
                                            >
                                                {
                                                    roles && roles.map((role) => <MenuItem value={role.id} key={role.id}>{role.name} </MenuItem>)
                                                }
                                            </Select>
                                        </FormControl>
                                    <FormControl sx={{display:"flex",flexDirection:"row",mx:1,flex:0.8,alignItems:"self-end"}}>
                                        <FormControl sx={{flex:1, display:'flex'}}>
                                            <FormLabel sx={{ml:1, mb:0.5}}>Fecha</FormLabel>
                                            <LocalizationProvider dateAdapter={AdapterDateFns} >
                                                <Box sx={{backgroundColor:lightGrayColor,borderRadius:9}}>
                                                    <DatePicker
                                                        inputFormat="dd/MM/yyyy"
                                                        value={resourceStartDates[_index]}
                                                        onChange={(selectedDate: Date | null) => { dispatch(setResourceStageStartDate({selectedDate, assignment, _index})) && handleStartDateChange(_index, selectedDate!) }}
                                                        renderInput={(params: TextFieldProps) => <TextField {...params} />}
                                                    />
                                                </Box>
                                            </LocalizationProvider>
                                        </FormControl>
                                        <FormControl sx={{flex:1,mx:1, display:'flex'}}>
                                            <FormControl>
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <Box sx={{backgroundColor:lightGrayColor,borderRadius:9}}>
                                                        <DatePicker
                                                            inputFormat="dd/MM/yyyy"
                                                            value={resourceEndDates[_index]}
                                                            onChange={(selectedDate: Date | null) => { dispatch(setResourceStageEndDate({selectedDate, assignment, _index})) && handleEndDateChange(_index, selectedDate!) }}
                                                            renderInput={(params: TextFieldProps) => <TextField {...params} />}
                                                        />
                                                    </Box>
                                                </LocalizationProvider>
                                            </FormControl>
                                        </FormControl>
                                    </FormControl>
                                    <FormControl sx={{display:"flex",flexDirection:"row",flex:0.1, alignItems:"center", justifyContent:"center", pt:2.2, pl:2}}>
                                        <IconButton children={<CopyIcon />} onClick={() => {
                                            duplicateProjectResource(assignment.resourceId);
                                        }}
                                    />
                        </FormControl>
                                </Box>)
                        }) : <Box my={3}></Box>
                    }
                </DialogContent>

                <Box sx={styles.secondGreyLine}>
                    <IconButton children={<Add />} onClick={() => {dispatch(addAssignment())}}
                                sx={{zIndex:10,position:"absolute", right:"-5px",top:"-20px",backgroundColor:greenColor,color:whiteColor,"&:hover":{backgroundColor:darkGreenColor}}}/>
                </Box>

                <Box display={'flex'} sx={{mt:5,ml:2, color: errors.stageText? redColor : "None"}}>
                    (*) Campos obligatorios <br/>
                    (**) El proyecto debe tener al menos un recurso asignado
                </Box>

                <Box display={'flex'} sx={{justifyContent:'space-between'}}>
                    <Box display={'flex'}>
                        <Button sx={{backgroundColor:redColor,color:whiteColor,width:120,alignSelf:"end",mr:2, mt:5,'&:hover': {backgroundColor: darkGreenColor,}}}
                                onClick={onClose}>Cancelar</Button>
                    </Box>
                    <Box display={'flex'}>
                        <Button sx={{backgroundColor:greenColor,color:whiteColor,width:120,alignSelf:"end",mr:2, mt:5,'&:hover': {backgroundColor: darkGreenColor,}}}
                                onClick={() => {saveNewProjectStage()}}>Guardar</Button>
                    </Box>
                </Box>
            </DialogContent>

        </Dialog>
    )
}